import { useState, useEffect } from "react";
import { DateField } from "@/components/ui/date-field";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function DossierExportDialog({ open, onOpenChange, endpoint, filename, kinds = [] }) {
  const [sel, setSel] = useState({});
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setSel(Object.fromEntries(kinds.map((k) => [k.value, true]))); setFrom(""); setTo(""); } /* eslint-disable-next-line */ }, [open]);

  const toggle = (v) => setSel((s) => ({ ...s, [v]: !s[v] }));

  const download = async () => {
    const chosen = kinds.filter((k) => sel[k.value]).map((k) => k.value);
    if (chosen.length === 0) { toast.error("Kies minstens één onderdeel"); return; }
    const params = new URLSearchParams();
    if (chosen.length < kinds.length) params.set("kinds", chosen.join(","));
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    setBusy(true);
    try {
      const res = await api.get(`${endpoint}?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
      onOpenChange(false);
    } catch { toast.error("Dossier genereren mislukt"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dossier-export-dialog">
        <DialogHeader>
          <DialogTitle>Dossier exporteren (PDF)</DialogTitle>
          <DialogDescription>Kies welke onderdelen en welke periode in het PDF-dossier komen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Onderdelen</Label>
            <div className="grid grid-cols-2 gap-2">
              {kinds.map((k) => (
                <label key={k.value} className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer">
                  <Checkbox checked={!!sel[k.value]} onCheckedChange={() => toggle(k.value)} data-testid={`dossier-kind-${k.value}`} /> {k.label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Vanaf</Label><DateField testId="dossier-from" value={from} onChange={setFrom} /></div>
            <div><Label className="text-xs text-slate-500">Tot en met</Label><DateField testId="dossier-to" value={to} onChange={setTo} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" disabled={busy} onClick={download} data-testid="dossier-download"><Download className="h-4 w-4 mr-2" /> {busy ? "Bezig…" : "Download PDF"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
